import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import Delete from '@mui/icons-material/Delete'
import UploadFile from '@mui/icons-material/UploadFile'
import moment from 'moment'
import API from '../../../services/API.js'
import { GlobalContext } from '../../../App.js'

const KG_PER_STONE = 6.35029318
const KG_PER_POUND = 0.45359237
const CM_PER_INCH = 2.54

const round1 = (n) => Math.round(n * 10) / 10

const YesNo = ({ label, value, onChange }) => (
  <Stack
    direction={{ xs: 'column', sm: 'row' }}
    justifyContent="space-between"
    alignItems={{ xs: 'flex-start', sm: 'center' }}
    gap={1}
  >
    <Typography variant="body2" sx={{ flex: 1 }}>
      {label}
    </Typography>
    <ToggleButtonGroup
      exclusive
      size="small"
      color="primary"
      value={value === true ? 'Yes' : value === false ? 'No' : null}
      onChange={(e, newValue) => {
        if (newValue) onChange(newValue === 'Yes')
      }}
    >
      <ToggleButton value="Yes" sx={{ px: 3 }}>
        Yes
      </ToggleButton>
      <ToggleButton value="No" sx={{ px: 3 }}>
        No
      </ToggleButton>
    </ToggleButtonGroup>
  </Stack>
)

const SectionCard = ({ title, subheader, children }) => (
  <Card elevation={0} variant="outlined" sx={{ borderRadius: 2 }}>
    <CardHeader
      title={title}
      subheader={subheader}
      titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
    />
    <Divider />
    <CardContent>{children}</CardContent>
  </Card>
)

const bmiCategory = (bmi) => {
  if (!bmi) return null
  if (bmi < 18.5) return { label: 'Underweight', color: 'warning' }
  if (bmi < 25) return { label: 'Healthy range', color: 'success' }
  if (bmi < 30) return { label: 'Overweight', color: 'warning' }
  return { label: 'Obese range', color: 'error' }
}

const MyOccupationalHealth = () => {
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState(null)
  const [settings, setSettings] = useState(null)
  const [orgName, setOrgName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [uploading, setUploading] = useState(false)

  // Biometrics kept canonically in metric; imperial fields are derived
  const [weightUnit, setWeightUnit] = useState('kg')
  const [heightUnit, setHeightUnit] = useState('cm')
  const [weightKg, setWeightKg] = useState('')
  const [heightCm, setHeightCm] = useState('')
  const [stones, setStones] = useState('')
  const [pounds, setPounds] = useState('')
  const [feet, setFeet] = useState('')
  const [inches, setInches] = useState('')

  const [gpDetails, setGpDetails] = useState('')
  const [disability, setDisability] = useState({ has: null, details: '' })
  const [allergies, setAllergies] = useState({ has: null, details: '' })
  const [medications, setMedications] = useState('')
  const [pregnant, setPregnant] = useState(null)
  const [correctiveLenses, setCorrectiveLenses] = useState(null)
  const [conditions, setConditions] = useState([])
  const [immunisations, setImmunisations] = useState({})
  const [evidence, setEvidence] = useState([])
  const [fitness, setFitness] = useState({})
  const [covidRisks, setCovidRisks] = useState([])
  const [customAnswers, setCustomAnswers] = useState({})
  const [healthSafetyAgreed, setHealthSafetyAgreed] = useState(false)

  const fileInputRef = useRef(null)
  const { setToast } = useContext(GlobalContext)

  const load = () => {
    setLoading(true)
    Promise.all([
      API.get('/occupational-health/my-status'),
      API.get('/settings?select=occupationalHealthSettings orgName'),
    ])
      .then(([statusRes, settingsRes]) => {
        if (statusRes.status === 200) setStatus(statusRes.data)
        if (settingsRes.status === 200) {
          setSettings(settingsRes.data?.occupationalHealthSettings ?? {})
          setOrgName(settingsRes.data?.orgName ?? 'your organisation')
        }
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
  }, [])

  const bmi = useMemo(() => {
    const w = Number(weightKg)
    const h = Number(heightCm)
    if (!w || !h) return null
    return round1(w / (h / 100) ** 2)
  }, [weightKg, heightCm])

  const setMetricWeight = (kg) => {
    setWeightKg(kg)
    const kgNum = Number(kg)
    if (kgNum > 0) {
      const totalPounds = kgNum / KG_PER_POUND
      setStones(String(Math.floor(totalPounds / 14)))
      setPounds(String(round1(totalPounds % 14)))
    }
  }

  const setImperialWeight = (newStones, newPounds) => {
    setStones(newStones)
    setPounds(newPounds)
    const st = Number(newStones) || 0
    const lb = Number(newPounds) || 0
    if (st > 0 || lb > 0) {
      setWeightKg(String(round1(st * KG_PER_STONE + lb * KG_PER_POUND)))
    }
  }

  const setMetricHeight = (cm) => {
    setHeightCm(cm)
    const cmNum = Number(cm)
    if (cmNum > 0) {
      const totalInches = cmNum / CM_PER_INCH
      setFeet(String(Math.floor(totalInches / 12)))
      setInches(String(round1(totalInches % 12)))
    }
  }

  const setImperialHeight = (newFeet, newInches) => {
    setFeet(newFeet)
    setInches(newInches)
    const ft = Number(newFeet) || 0
    const inch = Number(newInches) || 0
    if (ft > 0 || inch > 0) {
      setHeightCm(String(round1((ft * 12 + inch) * CM_PER_INCH)))
    }
  }

  const handleEvidenceUpload = async (e) => {
    const files = Array.from(e.target.files ?? [])
    if (files.length === 0) return
    setUploading(true)
    try {
      for (const file of files) {
        const extension = file.type.split('/')[1]
        const urlRes = await API.post('/gets3uploadurl', {
          folder: 'employees/occupational-health',
          extension,
        })
        if (urlRes.status !== 200) continue
        const { url, key } = urlRes.data
        const uploadRes = await API.put(url, file, {
          headers: { 'Content-Type': file.type },
        })
        if (uploadRes.status === 200) {
          setEvidence((prev) => [...prev, { title: file.name, s3key: key }])
        }
      }
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleEvidenceDelete = (s3key) => {
    setEvidence((prev) => prev.filter((f) => f.s3key !== s3key))
    API.post('/deletes3object', { key: s3key })
  }

  const isAuto = (item) =>
    (item.key === 'age50' && settings?.autoAgeRisk !== false) ||
    (item.key === 'pregnant' && settings?.autoPregnancyRisk !== false) ||
    (item.key === 'bmi40' && settings?.autoBmiRisk !== false)

  // Preview of what the auto items will resolve to (age is server-side only)
  const autoPreview = (item) => {
    if (item.key === 'pregnant') return pregnant === true
    if (item.key === 'bmi40') return !!bmi && bmi > 40
    return null
  }

  const immunisationItems = settings?.immunisations ?? []
  const fitnessItems = settings?.fitnessChecks ?? []
  const conditionItems = settings?.conditions ?? []
  const covidRiskItems = settings?.covidRisks ?? []
  const customItems = settings?.customQuestions ?? []

  const missing = []
  if (!Number(weightKg) || !Number(heightCm)) missing.push('height and weight')
  if (disability.has === null) missing.push('disability question')
  if (allergies.has === null) missing.push('allergy question')
  if (pregnant === null) missing.push('pregnancy question')
  if (correctiveLenses === null) missing.push('corrective lenses question')
  if (immunisationItems.some((item) => immunisations[item.label] === undefined))
    missing.push('immunisations')
  if (fitnessItems.some((item) => fitness[item.label] === undefined))
    missing.push('fitness requirements')
  if (customItems.some((item) => customAnswers[item.question] === undefined))
    missing.push('additional questions')
  if (!healthSafetyAgreed) missing.push('health and safety declaration')

  const handleSubmit = () => {
    setSubmitting(true)
    API.post('/occupational-health', {
      biometrics: { weightKg: Number(weightKg), heightCm: Number(heightCm) },
      gpDetails,
      disability: {
        has: disability.has === true,
        details: disability.has ? disability.details : '',
      },
      allergies: {
        has: allergies.has === true,
        details: allergies.has ? allergies.details : '',
      },
      medications,
      pregnant: pregnant === true,
      correctiveLenses: correctiveLenses === true,
      conditions,
      immunisations: immunisationItems.map((item) => ({
        label: item.label,
        immunised: immunisations[item.label] === true,
      })),
      evidence,
      fitness: fitnessItems.map((item) => ({
        label: item.label,
        able: fitness[item.label] === true,
      })),
      covidRisks,
      customAnswers: customItems.map((item) => ({
        question: item.question,
        answer: customAnswers[item.question] === true ? 'Yes' : 'No',
      })),
      healthSafetyAgreed,
    })
      .then((res) => {
        if (res.status === 200) {
          setSubmitted(true)
          setToast({ message: 'Occupational Health Screening Submitted' })
          load()
        } else {
          setToast({
            severity: 'error',
            message:
              res.data?.error ??
              'Something went wrong submitting your screening.',
          })
        }
      })
      .finally(() => setSubmitting(false))
  }

  if (loading) {
    return (
      <Stack gap={2}>
        <Typography variant="title2">Occupational Health</Typography>
        <LinearProgress />
      </Stack>
    )
  }

  if (!status?.enabled) {
    return (
      <Stack gap={2}>
        <Typography variant="title2">Occupational Health</Typography>
        <Alert severity="info">
          Occupational health screening is not currently enabled for your
          organisation.
        </Alert>
      </Stack>
    )
  }

  // The form stays open while an appraisal needs a screening confirm/update,
  // even when voluntary recompletion is off
  const upToDateAndClosed =
    !status.due &&
    status.allowSelfRecomplete === false &&
    !status.appraisalRefreshNeeded

  return (
    <Stack gap={2} pb={4}>
      <Typography variant="title2">Occupational Health</Typography>

      {submitted && (
        <Alert severity="success">
          Thank you — your occupational health screening has been submitted.
          You'll be asked to confirm or update it as part of each appraisal.
        </Alert>
      )}

      {!submitted && status.due && (
        <Alert severity="warning">
          {status.requested
            ? `${orgName} has asked you to complete a new occupational health screening.`
            : 'You have not completed an occupational health screening yet. Please complete it below.'}
        </Alert>
      )}

      {!submitted && status.appraisalRefreshNeeded && (
        <Alert severity="info">
          Your current appraisal includes an occupational health check —
          submitting an updated screening here will complete that step of your
          appraisal.
        </Alert>
      )}

      {!submitted && !status.due && (
        <Alert severity="success">
          Your occupational health screening is complete
          {status.lastSubmittedAt &&
            ` (last completed ${moment(status.lastSubmittedAt).format('Do MMM YYYY')})`}
          . You'll confirm or update it as part of each appraisal.
          {status.allowSelfRecomplete !== false &&
            ' You can also complete a new screening at any time below.'}
        </Alert>
      )}

      {upToDateAndClosed || submitted ? null : (
        <>
          <SectionCard
            title="Height & Weight"
            subheader="Used to calculate your BMI. You can enter these in metric or imperial units."
          >
            <Stack spacing={2}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ sm: 'center' }}
              >
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={heightUnit}
                  onChange={(e, v) => v && setHeightUnit(v)}
                >
                  <ToggleButton value="cm">cm</ToggleButton>
                  <ToggleButton value="ftin">ft & in</ToggleButton>
                </ToggleButtonGroup>
                {heightUnit === 'cm' ? (
                  <TextField
                    type="number"
                    size="small"
                    label="Height (cm)"
                    value={heightCm}
                    onChange={(e) => setMetricHeight(e.target.value)}
                  />
                ) : (
                  <>
                    <TextField
                      type="number"
                      size="small"
                      label="Feet"
                      value={feet}
                      onChange={(e) => setImperialHeight(e.target.value, inches)}
                    />
                    <TextField
                      type="number"
                      size="small"
                      label="Inches"
                      value={inches}
                      onChange={(e) => setImperialHeight(feet, e.target.value)}
                    />
                  </>
                )}
              </Stack>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ sm: 'center' }}
              >
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={weightUnit}
                  onChange={(e, v) => v && setWeightUnit(v)}
                >
                  <ToggleButton value="kg">kg</ToggleButton>
                  <ToggleButton value="stlb">st & lb</ToggleButton>
                </ToggleButtonGroup>
                {weightUnit === 'kg' ? (
                  <TextField
                    type="number"
                    size="small"
                    label="Weight (kg)"
                    value={weightKg}
                    onChange={(e) => setMetricWeight(e.target.value)}
                  />
                ) : (
                  <>
                    <TextField
                      type="number"
                      size="small"
                      label="Stones"
                      value={stones}
                      onChange={(e) => setImperialWeight(e.target.value, pounds)}
                    />
                    <TextField
                      type="number"
                      size="small"
                      label="Pounds"
                      value={pounds}
                      onChange={(e) => setImperialWeight(stones, e.target.value)}
                    />
                  </>
                )}
              </Stack>

              {bmi && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2">
                    Calculated BMI: <strong>{bmi}</strong>
                  </Typography>
                  {bmiCategory(bmi) && (
                    <Chip
                      size="small"
                      label={bmiCategory(bmi).label}
                      color={bmiCategory(bmi).color}
                      variant="outlined"
                    />
                  )}
                </Stack>
              )}
            </Stack>
          </SectionCard>

          <SectionCard title="Your GP">
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Name and address of your GP / surgery"
              value={gpDetails}
              onChange={(e) => setGpDetails(e.target.value)}
            />
          </SectionCard>

          <SectionCard title="General Health">
            <Stack spacing={2}>
              <YesNo
                label="Do you have a disability?"
                value={disability.has}
                onChange={(has) => setDisability((prev) => ({ ...prev, has }))}
              />
              {disability.has === true && (
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Please detail the extent of your disabilities"
                  value={disability.details}
                  onChange={(e) =>
                    setDisability((prev) => ({
                      ...prev,
                      details: e.target.value,
                    }))
                  }
                />
              )}
              <YesNo
                label="Do you have an allergy?"
                value={allergies.has}
                onChange={(has) => setAllergies((prev) => ({ ...prev, has }))}
              />
              {allergies.has === true && (
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Please give details of your allergies"
                  value={allergies.details}
                  onChange={(e) =>
                    setAllergies((prev) => ({
                      ...prev,
                      details: e.target.value,
                    }))
                  }
                />
              )}
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Please list any regular prescribed medications that you take"
                value={medications}
                onChange={(e) => setMedications(e.target.value)}
              />
              <YesNo
                label="Are you currently pregnant?"
                value={pregnant}
                onChange={setPregnant}
              />
              <YesNo
                label="Do you wear corrective lenses to drive vehicles?"
                value={correctiveLenses}
                onChange={setCorrectiveLenses}
              />
            </Stack>
          </SectionCard>

          <SectionCard
            title="Medical Conditions"
            subheader="Do you have any of the following? Tick all that apply."
          >
            <Stack>
              {conditionItems.map((item) => (
                <FormControlLabel
                  key={item.label}
                  control={
                    <Checkbox
                      checked={conditions.includes(item.label)}
                      onChange={(e) =>
                        setConditions((prev) =>
                          e.target.checked
                            ? [...prev, item.label]
                            : prev.filter((label) => label !== item.label),
                        )
                      }
                    />
                  }
                  label={item.label}
                />
              ))}
            </Stack>
          </SectionCard>

          <SectionCard
            title="Immunisations"
            subheader="Have you been immunised/vaccinated against the following?"
          >
            <Stack spacing={1.5}>
              {immunisationItems.map((item) => (
                <YesNo
                  key={item.label}
                  label={item.label}
                  value={immunisations[item.label] ?? null}
                  onChange={(value) =>
                    setImmunisations((prev) => ({
                      ...prev,
                      [item.label]: value,
                    }))
                  }
                />
              ))}
              <Divider />
              <Typography variant="body2" color="text.secondary">
                If you have evidence of your immunisations (e.g. vaccination
                cards or letters), you can upload it here.
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  variant="outlined"
                  size="small"
                  component="label"
                  startIcon={
                    uploading ? <CircularProgress size={16} /> : <UploadFile />
                  }
                  disabled={uploading}
                >
                  Upload Evidence
                  <input
                    ref={fileInputRef}
                    hidden
                    multiple
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={handleEvidenceUpload}
                  />
                </Button>
              </Stack>
              {evidence.map((file) => (
                <Stack
                  key={file.s3key}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                >
                  <Typography variant="body2">{file.title}</Typography>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleEvidenceDelete(file.s3key)}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          </SectionCard>

          <SectionCard
            title="Fitness"
            subheader="Can you complete the following?"
          >
            <Stack spacing={1.5}>
              {fitnessItems.map((item) => (
                <YesNo
                  key={item.label}
                  label={item.label}
                  value={fitness[item.label] ?? null}
                  onChange={(value) =>
                    setFitness((prev) => ({ ...prev, [item.label]: value }))
                  }
                />
              ))}
            </Stack>
          </SectionCard>

          <SectionCard
            title="Individual COVID-19 Risk Assessment"
            subheader={`All new ${orgName} staff are assessed for personal individual risk from COVID-19 infections. Please select any of the risks below in order that we are aware of your specific needs.`}
          >
            <Stack>
              {covidRiskItems.map((item) => {
                const auto = isAuto(item)
                const preview = auto ? autoPreview(item) : null
                return (
                  <Stack
                    key={item.label}
                    direction="row"
                    alignItems="center"
                    spacing={1}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          disabled={auto}
                          checked={
                            auto
                              ? preview === true
                              : covidRisks.includes(item.label)
                          }
                          onChange={(e) =>
                            setCovidRisks((prev) =>
                              e.target.checked
                                ? [...prev, item.label]
                                : prev.filter((label) => label !== item.label),
                            )
                          }
                        />
                      }
                      label={item.label}
                    />
                    {auto && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label="Filled in automatically"
                      />
                    )}
                  </Stack>
                )
              })}
            </Stack>
          </SectionCard>

          {customItems.length > 0 && (
            <SectionCard title="Additional Questions">
              <Stack spacing={1.5}>
                {customItems.map((item) => (
                  <YesNo
                    key={item.question}
                    label={item.question}
                    value={customAnswers[item.question] ?? null}
                    onChange={(value) =>
                      setCustomAnswers((prev) => ({
                        ...prev,
                        [item.question]: value,
                      }))
                    }
                  />
                ))}
              </Stack>
            </SectionCard>
          )}

          <SectionCard
            title="Health & Safety Requirements"
            subheader={`This section allows us to monitor your compliance with current health and safety legislation and allows us to ensure that we (${orgName}) can maintain your, your colleagues', and the public's safety.`}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={healthSafetyAgreed}
                  onChange={(e) => setHealthSafetyAgreed(e.target.checked)}
                />
              }
              label={`I confirm that I will adhere to any health and safety instructions given to me by ${orgName}.`}
            />
          </SectionCard>

          {missing.length > 0 && (
            <Alert severity="info">
              Still to complete: {missing.join(', ')}.
            </Alert>
          )}

          <Button
            size="large"
            variant="contained"
            color="success"
            disabled={missing.length > 0 || submitting}
            onClick={handleSubmit}
          >
            {submitting ? 'Submitting...' : 'Submit Screening'}
          </Button>
        </>
      )}
    </Stack>
  )
}

export default MyOccupationalHealth
